import { HubSpotFetchType } from '../../enums/hubspot/hubspot-fetch-type';
import {
	HubSpotAuthType,
	HubSpotConnectionDetails,
} from '../../interfaces/hubspot/hubspot-connection-details';
import { DestinationTaskConfigBase } from '../config-objects/destination-service-config-base';
import { IfRuleGroup } from '../if-gate/if-rule-group';

/**
 * Configuration for a HubspotGetObjects task — retrieves HubSpot objects using
 * one of three fetch strategies (WhereFilter, GetAssociations, GetAssociationsById).
 * Supports pagination, association loading, retries, and expression-based filtering.
 */
export class HubSpotGetObjectsConfig
	extends DestinationTaskConfigBase
	implements HubSpotConnectionDetails
{
	/**
	 * When true, treats accessToken as a flow variable expression resolved at runtime.
	 * When false, accessToken is used as a literal string.
	 */
	useVariableToken?: boolean;

	/**
	 * HubSpot private app access token. Supports expression syntax.
	 * Leave blank to use the iHub credential store.
	 * @default ''
	 */
	accessToken = '';

	/** Chooses between a static/variable Private App token vs a reusable Web App OAuth connection. @default HubSpotAuthType.PrivateAppToken */
	authType: HubSpotAuthType = HubSpotAuthType.PrivateAppToken;
	/** Selected `OAuthConnectionConfig.id` (provider=HubSpot). WebApp auth only. */
	connectionID?: string;

	/**
	 * HubSpot object type to retrieve (e.g. 'contacts', 'companies', 'deals').
	 * @default 'Contacts'
	 */
	hubSpotObjectType = 'Contacts';

	/**
	 * HubSpot property name used as the ID when fetching by hubSpotObjectId.
	 * Leave blank to use the default HubSpot internal id.
	 * @default 'id'
	 */
	idProperty = 'id';

	/**
	 * Expression resolving to the HubSpot object ID to fetch per input record.
	 * Evaluated against each input record. Only used when fetchType = GetAssociationsById (1).
	 */
	hubSpotObjectId?: string;

	/**
	 * When true, stores the HubSpot forward-paging cursor in contextData under
	 * pagingTokenVariable after each fetch. Set to 'DONE' when no more pages remain.
	 * @default undefined (falsy)
	 */
	fetchByPage?: boolean;

	/**
	 * Maximum records to return per request. Must be 1–100 (HubSpot API limit).
	 * Supports a static number or an expression string.
	 * @default 10
	 */
	limit: number | string = 10;

	/**
	 * Starting paging cursor for this request. Typically left unset — the service defaults
	 * to reading from the pagingTokenVariable flow variable via expression.
	 */
	after?: string;

	/**
	 * Flow variable name used to read the starting cursor and store the next cursor.
	 * @default 'HubSpotPageToken'
	 */
	pagingTokenVariable?: string;

	/**
	 * Array of HubSpot property API names to include in each returned record.
	 * Leave empty to use HubSpot defaults.
	 */
	properties?: string[];

	/**
	 * Controls which HubSpot API endpoint is used.
	 * GetAssociations (0) = getPage (no filter, paginated).
	 * GetAssociationsById (1) = getById per input record.
	 * WhereFilter (2) = CRM Search API with filter groups from ruleGroup.
	 * @default HubSpotFetchType.WhereFilter
	 */
	fetchType: HubSpotFetchType = HubSpotFetchType.WhereFilter;

	/**
	 * Filter criteria for WhereFilter fetchType. Uses the IfRuleGroup/IfRule structure.
	 * Translated to HubSpot CRM Search API filter groups at runtime.
	 * Leave ruleGroup.ruleGroup empty for no WHERE clause.
	 */
	ruleGroup: IfRuleGroup = new IfRuleGroup();

	/**
	 * Array of HubSpot association type names to include in the response
	 * (e.g. ['companies', 'deals']).
	 */
	associations?: string[];

	/**
	 * When true, retrieves archived (deleted) objects instead of active ones.
	 * @default undefined (falsy)
	 */
	archived?: boolean;

	/**
	 * When true, throws an error if no records are returned.
	 * When false/undefined, returns a single record with processingData = { message: 'No Data' }.
	 * @default undefined (falsy — silent empty result)
	 */
	emptyFetch?: boolean;

	/**
	 * Enables automatic retry on HubSpot API errors with exponential backoff.
	 * @default undefined (falsy)
	 */
	enableRetries?: boolean;

	/**
	 * Maximum number of retry attempts when enableRetries is true.
	 * @default 3
	 */
	retryLimit = 3;

	/**
	 * Initial delay in milliseconds before the first retry. Doubles on each subsequent
	 * attempt (exponential backoff).
	 * @default 500
	 */
	retryDelay = 500;

	constructor(init?: Partial<HubSpotGetObjectsConfig>) {
		super(init);
		if (init) {
			Object.assign(this, init);
		}
	}
}
